import React from 'react';
import { Helmet } from 'react-helmet';
import SeoSocialTags from '../components/SeoSocialTags';
import SolutionsWorkflowStepper from '../components/SolutionsWorkflowStepper';
import SolutionsFunnelGraphic from '../components/SolutionsFunnelGraphic';
import SolutionChildHero, { SOLUTION_CHILD_HERO_ILLUSTRATIONS } from '../components/SolutionChildHero';
import './SolutionChildPage.css';
import './SolutionAutomatedTriagePage.css';
import '../components/Features.css';

const CANONICAL_URL = 'https://circumvent.io/solutions/automated-triage';
const PAGE_TITLE = 'Automated vulnerability triage | Circumvent';
const PAGE_DESCRIPTION =
  'Correlate Cloud and AppSec findings, then validate. 95% noise reduction through contextual prioritization, grouping by action, and logged verdicts.';
const NEXT_STEPS_BACKGROUND = `${process.env.PUBLIC_URL}/images/backgrounds/gradient-multi-bg.webp`;
const NEXT_STEPS_SECTION_STYLE = {
  '--cta-bg-image': `url(${NEXT_STEPS_BACKGROUND})`,
};

const INSIGHT_INVESTIGATION_TILES = [
  {
    title: 'Correlate',
    body:
      'Group similar resource findings by title and versions so you see a single actionable list incorporating findings from multiple security vendors\' tools.',
    iconSrc: '/images/features/icon_Insights01_correlate.svg',
  },
  {
    title: 'Contextualize',
    body:
      'Attach findings to Resources and trace their alignment to Services and Applications to visualize risk hotspots. Detect environment and ownership.',
    iconSrc: '/images/features/icon_Insights02_contextualize.svg',
  },
  {
    title: 'Enrichment',
    body:
      'Enrich CVEs with descriptions and vectors. Understand and explain the expected business impact to stakeholders if the reported risk were to be exploited.',
    iconSrc: '/images/features/icon_Insights03_enrichment.svg',
  },
  {
    title: 'Exposure detection',
    body:
      'Check what is actually reachable from the internet or sensitive trust boundaries, not the headline severity alone.',
    iconSrc: '/images/features/icon_Insights04_exposure.svg',
  },
  {
    title: 'Attack path detection',
    body:
      'Trace plausible abuse sequences from entry points to critical data or control planes your program cares about.',
    iconSrc: '/images/features/icon_Insights05_attackpath.svg',
  },
  {
    title: 'Blast radius',
    body:
      'Understand how a single finding could lead to an exploit in one area but also expose other Resources or Services. Prioritize systemic risks higher than individual issues.',
    iconSrc: '/images/features/icon_Insights06_blastradius.svg',
  },
  {
    title: 'Dependency validation',
    body:
      'Ground findings in dependency trees so you see how packages and libraries chain together, where a vulnerable version sits, and what that path means for scope and ownership.',
    iconSrc: '/images/features/icon_Insights07_dependency.svg',
  },
  {
    title: 'Exploitability detection',
    body:
      'When a risk sits inside a library dependency, map it to concrete exploitable functions, then check whether those functions are actually referenced and used in your code so noise drops.',
    iconSrc: '/images/features/icon_Insights08_exploitability.svg',
  },
  {
    title: 'Related risk history',
    body:
      "Surface similar or repeated findings to look for repeatable patterns for triage, risk acceptance, remediation, so the same issues aren't escalated repeatedly.",
    iconSrc: '/images/features/icon_Insights09_relatedrisk.svg',
  },
  {
    title: 'Compensating controls',
    body:
      'Record mitigations and guardrails that change real-world exploitability when a control already limits exposure.',
    iconSrc: '/images/features/icon_Insights10_compensating.svg',
  },
  {
    title: 'Determination',
    body:
      'Close the loop with decision from the agent orchestrator on the reported risk. Decisions factor in all sub-agent investigation evidence.',
    iconSrc: '/images/features/icon_Insights11_closeloop.svg',
  },
];

const INSIGHT_OUTCOME_HEADING =
  'Circumvent ingests security findings from across code and cloud, and triages them through agentic investigations to determine real business risk.';

const INSIGHT_OUTCOME_POINTS = [
  {
    id: 'queue',
    body: 'One queue fed by ingestion, shaped by Insights, and gated by investigation before owners see work.',
    iconSrc: '/images/features/icon_funnel.svg',
  },
  {
    id: 'context',
    body: "Shared context for Applications and Services so engineering escalations match your organization's ownership.",
    iconSrc: '/images/features/icon_sharedcontext.svg',
  },
  {
    id: 'cycles',
    body:
      'Fewer wasted engineering cycles on issues the business would not prioritize or the agent has disproved in context.',
    iconSrc: '/images/features/icon_cycle.svg',
  },
];

const SolutionAutomatedTriagePage = () => (
  <>
    <Helmet>
      <title>{PAGE_TITLE}</title>
      <meta name="description" content={PAGE_DESCRIPTION} />
      <link rel="canonical" href={CANONICAL_URL} />
    </Helmet>
    <SeoSocialTags
      title={PAGE_TITLE}
      description={PAGE_DESCRIPTION}
      url={CANONICAL_URL}
    />

    <main className="page page--light solution-child-page">
      <SolutionChildHero
        headingId="solution-automated-triage-hero-heading"
        eyebrow="Automated Triage"
        title="Turn fragmented scans into owner-ready outcomes"
        lead="Engineering teams need one queue, traceable intelligence, and prioritization that respects Applications and Services context and ownership. Circumvent unifies data from multiple clouds and tools into Insights, enriched by agentic investigation to verify and measure risk."
        illustration={SOLUTION_CHILD_HERO_ILLUSTRATIONS.triage}
      />

      <section
        className="page-section page-section--white"
        aria-labelledby="solution-automated-triage-ingestion-heading"
      >
        <div className="page-container page-container--stack">
          <div className="section-panel__header section-panel__header--spaced">
            <p className="section-panel__eyebrow">Ingestion</p>
            <h2 id="solution-automated-triage-ingestion-heading" className="section-panel__title">
              Cloud application security data in one correlated view
            </h2>
            <p className="section-panel__intro solution-child-page__section-dek">
              Findings normalize to the Open Cybersecurity Schema Framework (OCSF) so the same risks across
              vendors are correlated. Your team gets one
              operational view to defend, not parallel spreadsheets and consoles. Data aligns to
              Application context and environment, without orphaned identifiers.
            </p>
          </div>
          <SolutionsWorkflowStepper titleId="solution-automated-triage-workflow-heading" theme="light" showEyebrow={false} />
        </div>
      </section>

      <section
        className="page-section page-section--pixel-noise"
        aria-labelledby="solution-automated-triage-insights-heading"
      >
        <div className="page-container page-container--stack">
          <div className="section-panel__header section-panel__header--spaced">
            <p className="section-panel__eyebrow">Insights &amp; investigation</p>
            <h2 id="solution-automated-triage-insights-heading" className="section-panel__title">
              Insights investigated by Agents to triage and verify
            </h2>
            <p className="section-panel__intro solution-child-page__section-dek">
              Insights compress finding noise into patterns you can defend. Multi-agent approach to triage
              investigation to validate reported risk and verify potential impact.
            </p>
          </div>

          <div
            className="features-grid solution-automated-triage__insights-grid"
            role="list"
            aria-label="Insights and investigation capabilities"
          >
            {INSIGHT_INVESTIGATION_TILES.map((tile) => (
              <article key={tile.title} className="feature-card" role="listitem">
                <div className="feature-illustration">
                  <img src={tile.iconSrc} alt="" width={48} height={48} loading="lazy" decoding="async" />
                </div>
                <h3>{tile.title}</h3>
                <p>{tile.body}</p>
              </article>
            ))}
          </div>

          <div className="solution-automated-triage__outcomes">
            <p className="subhead solution-automated-triage__outcomes-heading">{INSIGHT_OUTCOME_HEADING}</p>
            <ul className="solution-automated-triage__outcomes-grid" role="list">
              {INSIGHT_OUTCOME_POINTS.map((point) => (
                <li key={point.id} className="solution-automated-triage__outcomes-item">
                  <span className="solution-automated-triage__outcomes-icon" aria-hidden="true">
                    <img src={point.iconSrc} alt="" width={48} height={48} loading="lazy" decoding="async" />
                  </span>
                  <p>{point.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section
        className="page-section page-section--white"
        aria-labelledby="solution-automated-triage-prioritization-heading"
      >
        <div className="page-container page-container--stack">
          <div className="section-panel__header section-panel__header--spaced solution-child-page__header--dek-to-outcomes">
            <p className="section-panel__eyebrow">Prioritization</p>
            <h2 id="solution-automated-triage-prioritization-heading" className="section-panel__title">
              Insights verified and prioritized by context
            </h2>
            <ul className="solution-child-page__outcomes solution-automated-triage__check-list">
              <li>
                Business context attributes elevate work that touches regulated data or revenue-critical
                Applications even when raw CVSS is flat across the backlog.
              </li>
              <li>
                Investigation verifies exposure, attack path, and blast radius, to downgrade or close noise
                with traceable rationale instead of silent suppression.
              </li>
              <li>
                Circumvent orders work when both signals agree you should interrupt engineering, not when
                either signal arrives alone.
              </li>
            </ul>
          </div>

          <div className="solution-automated-triage__risk-funnel">
            <SolutionsFunnelGraphic
              titleId="solution-automated-triage-funnel-heading"
              theme="light"
              showEyebrow={false}
            />
          </div>
        </div>
      </section>

      <section
        className="page-section page-section--white solution-child-page__cta-section solution-child-page__cta-section--image"
        aria-labelledby="solution-automated-triage-cta-heading"
        style={NEXT_STEPS_SECTION_STYLE}
      >
        <div className="page-container page-container--stack">
          <div className="page-section__header" aria-labelledby="solution-automated-triage-cta-heading">
            <h2 id="solution-automated-triage-cta-heading">Next steps</h2>
            <p className="solution-child-page__cta-lead">
              See Circumvent in action on your environment.
            </p>
            <p className="solution-child-page__cta-actions">
              <a href="/#contact" className="btn btn-solid">
                Book a demo
              </a>
            </p>
          </div>
        </div>
      </section>
    </main>
  </>
);

export default SolutionAutomatedTriagePage;
